/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Radio, RadioGroup, TextField, Typography2 } from '@coursera/cds-core';

import _t from 'i18n!nls/enterprise-admin-learners';

export type GroupOption = {
  id: string;
  name: string;
  slug: string;
};

type GroupSelectListProps = {
  groups: GroupOption[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
  /** Accessible group label for the radio list. */
  ariaLabel: string;
};

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  list: css`
    max-height: 280px;
    overflow-y: auto;
  `,
};

/**
 * Searchable single-select list of groups, shared by the All Learners "Add to group"
 * and "Assign content" pickers. Filtering is client-side over the already-fetched
 * (permission-scoped) group list.
 */
const GroupSelectList = ({ groups, selectedId, onSelect, ariaLabel }: GroupSelectListProps) => {
  const [search, setSearch] = React.useState('');
  const query = search.trim().toLowerCase();
  const filtered = query ? groups.filter((group) => group.name.toLowerCase().includes(query)) : groups;

  return (
    <div css={styles.root}>
      <TextField
        label={_t('Groups')}
        necessityIndicator="icon"
        value={search}
        placeholder={_t('Search group')}
        onChange={(event) => setSearch(event.target.value)}
        fullWidth
      />
      {filtered.length === 0 ? (
        <Typography2 component="p">{_t('No groups found.')}</Typography2>
      ) : (
        <div css={styles.list}>
          <RadioGroup
            value={selectedId ?? ''}
            name="group-select"
            aria-label={ariaLabel}
            onChange={(event) => onSelect(event.target.value)}
          >
            {filtered.map((group) => (
              <Radio key={group.id} value={group.id} label={group.name} />
            ))}
          </RadioGroup>
        </div>
      )}
    </div>
  );
};

export default GroupSelectList;
